import React, { useCallback, useEffect, useState } from 'react';
import { ventesAdd as api } from '../services/api';
import './Objectifs.css';
import './Stock.css';
import './ReferentielMarche.css';
import './Creances.css';   // cr-btn
import './ReferentielVentesAdd.css';

// ─── Référentiel des ventes additionnelles — écran d'administration ─────────
// Les familles du rapport APV — Ventes additionnelles (2026-10-06). Chaque
// famille classe les lignes par MOTIFS sur le libellé, en majuscules : les
// références des pièces ne sont pas fiables. Une ligne prend la première
// famille (ordre croissant) dont le motif passe et l'exclusion non ; la main
// d'œuvre se classe sur le libellé de son opération (motif MO, même
// exclusion), à défaut sur sa nature.
//
// L'aperçu montre, AVANT d'enregistrer, les libellés et les opérations que la
// règle retient sur les 12 derniers mois : c'est là qu'on repère un faux
// positif (« DISQUE EMBRAYAGE ») ou un oubli. Écriture réservée aux
// administrateurs ; chaque enregistrement est historisé et vide le cache.

const nf = new Intl.NumberFormat('fr-FR');
const nb = (v) => nf.format(Math.round(Number(v) || 0));
const NOUVELLE = { code: '', libelle: '', ordre: 100, motif: '', exclusion: '', motif_mo: '', actif: true, nouvelle: true };

export default function ReferentielVentesAdd() {
  const [familles, setFamilles] = useState(null);
  const [editable, setEditable] = useState(false);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [ouverte, setOuverte] = useState(null);   // code de la famille éditée
  const [brouillon, setBrouillon] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [attente, setAttente] = useState(false);

  const charger = useCallback(() => api.familles()
    .then((r) => { setFamilles(r.data.familles); setEditable(r.data.editable); })
    .catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement')), []);
  useEffect(() => { charger(); }, [charger]);

  const ouvrir = (fa) => { setOuverte(fa.code || '__nouvelle__'); setBrouillon({ ...fa, exclusion: fa.exclusion || '', motif_mo: fa.motif_mo || '' }); setApercu(null); setMessage(''); setErreur(''); };
  const voirApercu = async () => {
    setAttente(true); setErreur('');
    try { const r = await api.apercu(brouillon); setApercu(r.data); }
    catch (e) { setErreur(e.response?.data?.error || 'Aperçu impossible'); }
    finally { setAttente(false); }
  };
  const enregistrer = async () => {
    setAttente(true); setErreur('');
    try { await api.enregistrer(brouillon); setMessage(`« ${brouillon.libelle} » enregistrée.`); setOuverte(null); setApercu(null); await charger(); }
    catch (e) { setErreur(e.response?.data?.error || 'Enregistrement impossible'); }
    finally { setAttente(false); }
  };
  const champ = (k, props = {}) => (
    <input className="rva-input" value={brouillon[k] ?? ''} disabled={!editable} {...props}
      onChange={(e) => setBrouillon({ ...brouillon, [k]: props.type === 'number' ? e.target.value : e.target.value })} />
  );

  return (
    <main className="ob-page rm-page">
      <div className="ob-head">
        <h1>Ventes additionnelles — familles</h1>
        <p className="ob-sub">Règles de classement du rapport APV — Ventes additionnelles : motifs sur le libellé des pièces (en majuscules) et sur l'opération de la main d'œuvre (à défaut sa nature). L'exclusion vaut pour les deux.</p>
      </div>
      {erreur && <div className="ob-erreur">⚠️ {erreur}</div>}
      {message && <div className="ob-ok">{message}</div>}
      {!familles ? <div className="vt-skel" /> : (
        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Familles</h2>
            <div className="st-tools">
              <span className="hint">motifs = expressions régulières (| = ou, .* = n'importe quoi entre) · première famille qui passe, par ordre</span>
              {editable && <button type="button" className="cr-btn ghost" onClick={() => ouvrir(NOUVELLE)}>+ Ajouter une famille</button>}
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr><th>Ordre</th><th className="l">Famille</th><th className="l">Motif pièces</th><th className="l">Exclusion</th><th className="l">Motif MO</th><th>Active</th><th className="l">Modifiée</th></tr></thead>
              <tbody>
                {familles.map((fa) => (
                  <tr key={fa.code} className={`tbl-clic${ouverte === fa.code ? ' actif' : ''}`} onClick={() => ouvrir(fa)}>
                    <td>{fa.ordre}</td><td className="l">{fa.libelle}</td>
                    <td className="l"><code>{fa.motif}</code></td><td className="l"><code>{fa.exclusion || '—'}</code></td>
                    <td className="l"><code>{fa.motif_mo || '—'}</code></td><td>{fa.actif ? 'oui' : 'non'}</td>
                    <td className="l">{fa.maj_le ? new Date(fa.maj_le).toLocaleDateString('fr-FR') : ''}{fa.maj_par ? ` · ${fa.maj_par}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {brouillon && ouverte && (
        <div className="vt-card">
          <div className="vt-card-h">
            <h2>{brouillon.nouvelle ? 'Nouvelle famille' : brouillon.libelle}</h2>
            <div className="st-tools">
              <button type="button" className="cr-btn ghost" disabled={attente || !brouillon.motif} onClick={voirApercu}>Aperçu sur 12 mois</button>
              {editable && <button type="button" className="cr-btn" disabled={attente || !brouillon.motif || !brouillon.libelle || (brouillon.nouvelle && !brouillon.code)} onClick={enregistrer}>Enregistrer</button>}
              <button type="button" className="cr-btn ghost" onClick={() => { setOuverte(null); setApercu(null); }}>Fermer</button>
            </div>
          </div>
          <div className="vt-card-b">
            <table className="vt-tbl"><tbody>
              {brouillon.nouvelle && <tr><td className="l">Code (sans espace)</td><td className="l">{champ('code')}</td></tr>}
              <tr><td className="l">Libellé</td><td className="l">{champ('libelle')}</td></tr>
              <tr><td className="l">Ordre</td><td className="l">{champ('ordre', { type: 'number' })}</td></tr>
              <tr><td className="l">Motif pièces</td><td className="l">{champ('motif')}</td></tr>
              <tr><td className="l">Exclusion</td><td className="l">{champ('exclusion')}</td></tr>
              <tr><td className="l">Motif MO (opération)</td><td className="l">{champ('motif_mo')}</td></tr>
              <tr><td className="l">Active</td><td className="l"><input type="checkbox" checked={brouillon.actif !== false} disabled={!editable} onChange={(e) => setBrouillon({ ...brouillon, actif: e.target.checked })} /></td></tr>
            </tbody></table>
          </div>
          {apercu && (
            <div className="vt-card-b vt-tbl-wrap">
              <p className="hint">Sur 12 mois : {nb(apercu.total.lignes)} lignes de pièces, {nb(apercu.total.ca)} € HT. Les 40 libellés au plus fort CA, puis les opérations de MO retenues.</p>
              <table className="vt-tbl tbl-dense">
                <thead><tr><th className="l">Libellé de pièce retenu</th><th>Lignes</th><th>CA HT</th></tr></thead>
                <tbody>{apercu.libelles.map((l) => <tr key={l.libelle}><td className="l">{l.libelle}</td><td>{nb(l.lignes)}</td><td>{nb(l.ca)} €</td></tr>)}</tbody>
              </table>
              {apercu.natures.length > 0 && (
                <table className="vt-tbl tbl-dense">
                  <thead><tr><th className="l">Opération de MO retenue (payant)</th><th>Lignes</th><th>CA HT</th></tr></thead>
                  <tbody>{apercu.natures.map((l) => <tr key={l.nature}><td className="l">{l.nature}</td><td>{nb(l.lignes)}</td><td>{nb(l.ca)} €</td></tr>)}</tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </main>
  );
}
